import { StateCreator } from 'zustand'
import type { FFEStore } from '../types'
import type { Material } from '../../types/ffe'

export interface MaterialsSlice {
  addMaterial: (material: Omit<Material, 'id'>) => void
  updateMaterial: (id: string, updates: Partial<Material>) => void
  removeMaterial: (id: string) => void
  assignMaterialToLineItem: (
    lineItemId: string,
    materialId: string,
    quantity: number,
    materialType: 'finish' | 'accessory'
  ) => void
  removeMaterialFromLineItem: (lineItemId: string, materialId: string) => void
  updateLineItemMaterialQuantity: (
    lineItemId: string,
    materialId: string,
    quantity: number
  ) => void
  updateLineItemMaterialHideOnDocument: (
    lineItemId: string,
    materialId: string,
    hideOnDocument: boolean
  ) => void
}

export const createMaterialsSlice: StateCreator<
  FFEStore,
  [],
  [],
  MaterialsSlice
> = (set) => ({
  addMaterial: (material) =>
    set((state) => ({
      document: {
        ...state.document,
        materials: [
          ...state.document.materials,
          { ...material, id: crypto.randomUUID() }
        ]
      }
    })),

  updateMaterial: (id, updates) =>
    set((state) => ({
      document: {
        ...state.document,
        materials: state.document.materials.map((material) =>
          material.id === id ? { ...material, ...updates } : material
        )
      }
    })),

  removeMaterial: (id) =>
    set((state) => ({
      document: {
        ...state.document,
        materials: state.document.materials.filter(
          (material) => material.id !== id
        ),
        lineItems: state.document.lineItems.map((item) => ({
          ...item,
          materials: item.materials?.filter((m) => m.materialId !== id)
        }))
      }
    })),

  assignMaterialToLineItem: (lineItemId, materialId, quantity, materialType) =>
    set((state) => ({
      document: {
        ...state.document,
        lineItems: state.document.lineItems.map((item) =>
          item.id === lineItemId
            ? {
                ...item,
                materials: [
                  ...(item.materials || []),
                  {
                    id: crypto.randomUUID(),
                    materialId,
                    quantity,
                    materialType,
                    status: 'specified'
                  }
                ]
              }
            : item
        )
      }
    })),

  removeMaterialFromLineItem: (lineItemId, materialId) =>
    set((state) => ({
      document: {
        ...state.document,
        lineItems: state.document.lineItems.map((item) =>
          item.id === lineItemId
            ? {
                ...item,
                materials: item.materials?.filter(
                  (m) => m.materialId !== materialId
                )
              }
            : item
        )
      }
    })),

  updateLineItemMaterialQuantity: (lineItemId, materialId, quantity) =>
    set((state) => ({
      document: {
        ...state.document,
        lineItems: state.document.lineItems.map((item) =>
          item.id === lineItemId
            ? {
                ...item,
                materials: item.materials?.map((m) =>
                  m.materialId === materialId ? { ...m, quantity } : m
                )
              }
            : item
        )
      }
    })),
  updateLineItemMaterialHideOnDocument: (
    lineItemId,
    materialId,
    hideOnDocument
  ) =>
    set((state) => ({
      document: {
        ...state.document,
        lineItems: state.document.lineItems.map((item) =>
          item.id === lineItemId
            ? {
                ...item,
                materials: item.materials?.map((m) =>
                  m.materialId === materialId ? { ...m, hideOnDocument } : m
                )
              }
            : item
        )
      }
    }))
})
